import CobsConnection from './CobsConnection.js';
import Version from './Version.js';
import DeviceConnection from './DeviceConnection.js';
import _ from 'lodash';

export default class UsbDeviceConnection extends DeviceConnection {
  constructor(port, version) {
    super();
    this.connection      = new CobsConnection(port);
    this.connectionType  = 'usb';
    this.firmwareVersion = Version.parse(version);

    // Debounced update helpers — callers (e.g. Patches.vue) rely on .cancel().
    this.updateEffect        = _.debounce(this._updateEffect.bind(this), 500);
    this.updatePatchGlobals  = _.debounce(this._updatePatchGlobals.bind(this), 500);

    // Serialise all run() calls so concurrent invocations never overlap.
    this._queue = Promise.resolve();

    this._patchChangedCbs = [];
    this._tempoChangedCbs = [];
    this._parameterChangedCbs = [];
    this._effectMutedCbs = [];
    this._midiLearnEndedCbs = [];

    this.connection.onPushEvent = (msg) => {
      if (msg.event === 'patchChanged' && typeof msg.patch === 'number') {
        for (const cb of this._patchChangedCbs) cb(msg.patch);
      } else if (msg.event === 'tempoChanged') {
        for (const cb of this._tempoChangedCbs) cb(msg.bpm, msg.synced);
      } else if (msg.event === 'parameterChanged' && typeof msg.cc === 'number') {
        for (const cb of this._parameterChangedCbs) cb(msg);
      } else if (msg.event === 'effectMuted' && typeof msg.effectIndex === 'number') {
        for (const cb of this._effectMutedCbs) cb(msg);
      } else if (msg.event === 'midiLearnEnded' && typeof msg.reason === 'string') {
        for (const cb of this._midiLearnEndedCbs) cb(msg);
      }
    };
  }

  onPatchChanged(cb) {
    this._patchChangedCbs.push(cb);
  }

  onTempoChanged(cb) {
    this._tempoChangedCbs.push(cb);
  }

  // parameterChanged: a mapped incoming CC was applied on the device.
  // cb({ channel, cc, value }) — display-only, never dirties the model.
  onParameterChanged(cb) {
    this._parameterChangedCbs.push(cb);
  }

  // effectMuted: a mute mapping toggled an effect's mute on the device.
  // cb({ effectIndex, muted }) — externally driven only.
  onEffectMuted(cb) {
    this._effectMutedCbs.push(cb);
  }

  // midiLearnEnded: device-side MIDI Learn finished. cb({ reason, cc?, channel? }).
  onMidiLearnEnded(cb) {
    this._midiLearnEndedCbs.push(cb);
  }

  // Arm / disarm device-side MIDI Learn capture. The captured CC arrives via
  // onMidiLearnEnded, not as the command response.
  async startMidiLearn() {
    await this.run('MIDI_LEARN_START');
  }

  async cancelMidiLearn() {
    await this.run('MIDI_LEARN_CANCEL');
  }

  run(command, data = null, timeoutMs = undefined) {
    const task = () => this._run(command, data, timeoutMs);
    this._queue = this._queue.then(task, task);
    return this._queue;
  }

  async _run(command, data = null, timeoutMs = undefined) {
    await this.connection.open();
    return timeoutMs !== undefined
      ? this.connection.sendCommand(command, data, timeoutMs)
      : this.connection.sendCommand(command, data);
  }

  async disconnect() {
    await this.connection.close();
  }

  async _updateEffect(effect) {
    console.log('Updating effect:', effect);
    const response = await this.run('UPDATE_EFFECT', effect);
    console.log('Device response:', response);
  }

  async _updatePatchGlobals(globals) {
    console.log('Updating patch globals:', globals);
    await this.run('UPDATE_PATCH_GLOBALS', globals);
  }

  // Overrides the base-class stub to ask the device directly via GET_CAPABILITIES.
  // Falls back to empty capabilities if the firmware pre-dates the command.
  async getCapabilities() {
    try {
      const response = await this.run('GET_CAPABILITIES');
      const parsed = JSON.parse(response);
      if (parsed.capabilities_version !== 1) return { capabilities_version: 1, capabilities: {} };
      return parsed;
    } catch {
      return { capabilities_version: 1, capabilities: {} };
    }
  }

  async getWifiStatus() {
    const response = await this.run('GET_WIFI_STATUS');
    return JSON.parse(response);
  }

  async wifiReconnect(ssid, password) {
    // wifi_connect_timeout_s on the device can be up to 60s; use 70s here so
    // the COBS layer never times out before the device has had a chance to reply.
    const response = await this.run('WIFI_RECONNECT', { ssid, password }, 70000);
    return JSON.parse(response);
  }
}
